/**
 * Self-Hosted Socket Adapter
 *
 * Adapter implementation for self-hosted relay connections.
 * Provides unified interface with full daemon control capabilities.
 *
 * Part of Phase 2: Shared Interface & Adapter Pattern
 */

import { io, Socket } from 'socket.io-client';
import { BaseSocketAdapter } from './BaseSocketAdapter';
import type {
  SocketMode,
  AdapterCapabilities,
  ConnectionConfig,
  SessionOptions,
  NormalizedSession,
  PairingCode,
  AuthTokens,
  DaemonStatus,
  SyncedSession,
  HeartbeatConfig,
  HeartbeatState,
} from '../types/socketAdapter';
import type { MessageAttachment } from '../types/happy';
import {
  acquireConnection,
  refreshConnection,
  generateSocketId,
  releaseConnection,
} from '../services/socketConnectionMutex';
import {
  markEventProcessed,
  markSessionJoined,
  markSessionLeft,
  clearJoinedSessions,
} from '../utils/eventIdempotency';
import { selfHostedService } from '../services/selfHostedService';
import { useHappyStore } from '../store/happyStore';
import { useHybridStore } from '../store/hybridStore';
// NOTE: localStorage persistence removed - all data is server-managed

// ============================================
// Constants
// ============================================

const SOCKET_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: 3,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 8000,
  transports: ['websocket', 'polling'] as ('websocket' | 'polling')[],
};

const DEFAULT_HEARTBEAT_CONFIG: HeartbeatConfig = {
  pingIntervalMs: 30000,  // 30 seconds
  pongTimeoutMs: 15000,   // 15 seconds (increased from 5s for relay server latency)
  maxMissedPongs: 3,      // 3 missed pongs before reconnect (increased from 2)
};

// ============================================
// HeartbeatMonitor Class
// ============================================

/**
 * HeartbeatMonitor - Monitors relay server health via ping/pong
 *
 * Sends ping messages at regular intervals and tracks pong responses.
 * Triggers reconnection when too many pongs are missed.
 */
class HeartbeatMonitor {
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private missedPongs: number = 0;
  private lastPongTime: number = 0;
  private isActive: boolean = false;

  constructor(
    private config: HeartbeatConfig,
    private onPing: () => void,
    private onHealthy: () => void,
    private onUnhealthy: (missedPongs: number) => void,
    private onReconnecting: () => void
  ) {}

  /**
   * Start heartbeat monitoring
   */
  start(): void {
    this.stop(); // Clear any existing timers
    this.missedPongs = 0;
    this.lastPongTime = Date.now();
    this.isActive = true;

    console.log('[HeartbeatMonitor] Started with config:', this.config);

    // Start ping interval
    this.pingTimer = setInterval(() => this._sendPing(), this.config.pingIntervalMs);
  }

  /**
   * Stop heartbeat monitoring
   */
  stop(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.isActive = false;
    console.log('[HeartbeatMonitor] Stopped');
  }

  /**
   * Handle pong response from server
   */
  handlePong(): void {
    // Clear pong timeout
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }

    const wasUnhealthy = this.missedPongs >= 1;
    this.missedPongs = 0;
    this.lastPongTime = Date.now();

    // If we were unhealthy, notify that we're now healthy
    if (wasUnhealthy) {
      console.log('[HeartbeatMonitor] Connection recovered');
      this.onHealthy();
    }
  }

  /**
   * Send ping and start pong timeout
   */
  private _sendPing(): void {
    // Send ping via callback
    this.onPing();

    // Start pong timeout
    this.pongTimer = setTimeout(() => {
      this._handlePongTimeout();
    }, this.config.pongTimeoutMs);
  }

  /**
   * Handle pong timeout (no response within timeout period)
   */
  private _handlePongTimeout(): void {
    this.missedPongs++;
    console.log(`[HeartbeatMonitor] Pong timeout, missed count: ${this.missedPongs}`);

    if (this.missedPongs >= this.config.maxMissedPongs) {
      console.log('[HeartbeatMonitor] Max missed pongs reached, triggering reconnect');
      this.onReconnecting();
    } else {
      this.onUnhealthy(this.missedPongs);
    }
  }

  /**
   * Get time since last successful pong
   */
  getLastPongAge(): number {
    return Date.now() - this.lastPongTime;
  }

  /**
   * Check if connection is healthy
   */
  isHealthy(): boolean {
    return this.missedPongs < this.config.maxMissedPongs;
  }

  /**
   * Get current heartbeat state
   */
  getState(): HeartbeatState {
    return {
      isActive: this.isActive,
      missedPongs: this.missedPongs,
      lastPongTime: this.lastPongTime || null,
      isHealthy: this.isHealthy(),
    };
  }
}

// ============================================
// Self-Hosted Socket Adapter
// ============================================

export class SelfHostedSocketAdapter extends BaseSocketAdapter {
  readonly mode: SocketMode = 'self-hosted';

  readonly capabilities: AdapterCapabilities = {
    // Auth
    supportsPairing: true,
    supportsTokenRefresh: true,

    // Sessions
    supportsSpawnSession: true,
    supportsArchiveSession: true,
    supportsRestartSession: true,

    // Daemon
    supportsDaemonControl: true,
    supportsDaemonStatus: true,

    // Messages
    supportsMessageHistory: true,
    supportsStreaming: true,

    // Other
    supportsCommandCenter: false,
  };

  // Note: socket and socketId are now inherited from BaseSocketAdapter
  private serverUrl: string = '';
  private authToken: string | null = null;
  private daemonStatus: DaemonStatus = 'unknown';
  private webDeviceId: string | null = null;
  private linkedDaemonDeviceId: string | null = null;
  private heartbeatMonitor: HeartbeatMonitor | null = null;
  private heartbeatConfig: HeartbeatConfig = DEFAULT_HEARTBEAT_CONFIG;

  // Streaming message accumulation - tracks active response per session
  // Key: sessionId, Value: { messageId, content, startTime }
  // This allows thinking text between tool calls to accumulate into one message
  private activeStreams: Map<string, {
    messageId: string;
    content: string;
    startTime: number;
  }> = new Map();

  /**
   * Get or create a stable device ID for this web client
   * Used for registration with the relay server
   *
   * NOTE: localStorage removed - device ID is now read from cookies (set by server on pairing)
   * If no cookie exists, a temporary ID is generated for the session.
   */
  private getOrCreateDeviceId(): string {
    // Check memory first
    if (this.webDeviceId) {
      return this.webDeviceId;
    }

    // Check cookie (set by server on pairing)
    const cookieMatch = document.cookie.match(/vibe_device_id=([^;]+)/);
    if (cookieMatch) {
      this.webDeviceId = cookieMatch[1];
      return this.webDeviceId;
    }

    // Generate temporary session ID (will be replaced on successful pairing)
    const newId = `web-temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    this.webDeviceId = newId;
    return newId;
  }

  // ============================================
  // Connection Lifecycle
  // ============================================

  async connect(config: ConnectionConfig): Promise<boolean> {
    const { url, auth } = config;

    if (!url) {
      console.error('[SelfHostedAdapter] No relay URL provided');
      this.setConnectionState('error');
      this.emit('connection:error', 'No relay URL provided');
      return false;
    }

    this.serverUrl = url;
    this.authToken = auth?.token || null;

    // Generate socket ID for mutex
    const newSocketId = generateSocketId('self-hosted');

    // MUTEX: Acquire connection lock
    if (!acquireConnection('self-hosted', newSocketId)) {
      console.warn('[SelfHostedAdapter] Connection blocked - another socket type is active');
      this.setConnectionState('error');
      this.emit('connection:error', 'Another socket connection is already active');
      return false;
    }

    // Disconnect existing socket
    if (this.socket?.connected) {
      if (this.socketId) {
        releaseConnection(this.socketId);
      }
      this.socket.disconnect();
    }

    this.socketId = newSocketId;
    clearJoinedSessions();

    this.setConnectionState('connecting');

    const hasToken = !!this.authToken;
    console.log(`[SelfHostedAdapter] Connecting to ${this.serverUrl}... (${hasToken ? 'with auth' : 'pairing mode'})`);

    return new Promise((resolve) => {
      this.socket = io(this.serverUrl, {
        ...SOCKET_OPTIONS,
        auth: this.authToken ? { token: this.authToken, clientType: 'web-ui' } : undefined,
      });

      this.setupEventListeners();

      const timeout = setTimeout(() => {
        if (this.connectionState === 'connecting') {
          console.error('[SelfHostedAdapter] Connection timeout');
          this.setConnectionState('error');
          this.emit('connection:error', 'Connection timeout');
          resolve(false);
        }
      }, SOCKET_OPTIONS.timeout);

      this.socket.once('connect', () => {
        clearTimeout(timeout);
        this.setConnectionState('connected');

        // FIX for BUG-001: Register as web client BEFORE requesting sessions
        // The relay server only sets clientType='web' after receiving web:connect
        const deviceId = this.getOrCreateDeviceId();
        console.log('[SelfHostedAdapter] Emitting web:connect with deviceId:', deviceId);

        this.socket!.emit('web:connect', {
          deviceId,
          clientType: 'web',
          timestamp: Date.now(),
        });

        // Wait for web:connected response, then link to daemon and request sessions
        this.socket!.once('web:connected', (response: { success: boolean; deviceId?: string }) => {
          console.log('[SelfHostedAdapter] web:connected response:', response);

          if (response.success) {
            // BUG-001 FIX Part 2: Link to daemon before requesting sessions
            // The relay only returns sessions for linked daemons
            this.linkToDaemonAndRequestSessions();

            // Start heartbeat monitoring after successful connection
            this.initializeHeartbeatMonitor();

            // SYNC: Connect selfHostedService singleton for session title updates
            // The store uses selfHostedService for updateSessionTitle() which needs socket
            this.syncSelfHostedService();
          }
        });

        resolve(true);
      });

      this.socket.once('connect_error', (error) => {
        clearTimeout(timeout);
        console.error('[SelfHostedAdapter] Connection error:', error.message);
        this.setConnectionState('error');
        this.emit('connection:error', error.message);
        resolve(false);
      });
    });
  }

  disconnect(): void {
    // Stop heartbeat monitoring
    if (this.heartbeatMonitor) {
      this.heartbeatMonitor.stop();
      this.heartbeatMonitor = null;
    }

    if (this.socket) {
      console.log('[SelfHostedAdapter] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.socketId) {
      releaseConnection(this.socketId);
      this.socketId = null;
    }

    clearJoinedSessions();
    this.daemonStatus = 'unknown';
    this.cleanup();
  }

  // ============================================
  // Event Listeners Setup
  // ============================================

  private setupEventListeners(): void {
    if (!this.socket) return;

    // CRITICAL: Remove all existing listeners before adding new ones to prevent stacking
    // This fixes duplicate message issues on reconnect (BUG-013)
    this.socket.removeAllListeners();

    // P3-T1: Setup common event listeners from BaseSocketAdapter
    // Pass mode-specific callbacks for disconnect and reconnect
    this.setupCommonEventListeners({
      onDisconnect: (_reason: string) => {
        // Stop heartbeat on disconnect
        if (this.heartbeatMonitor) {
          this.heartbeatMonitor.stop();
        }
        // Reset daemon status
        this.daemonStatus = 'unknown';
      },
      onReconnect: () => {
        // BUG-003 FIX: Re-establish daemon link after reconnect
        // Without this, message responses won't be received after page refresh
        const deviceId = this.getOrCreateDeviceId();
        console.log('[SelfHostedAdapter] Re-emitting web:connect after reconnect:', deviceId);

        this.socket!.emit('web:connect', {
          deviceId,
          clientType: 'web',
          timestamp: Date.now(),
        });

        // Wait for acknowledgment, then re-link to daemon
        this.socket!.once('web:connected', (response: { success: boolean; deviceId?: string }) => {
          console.log('[SelfHostedAdapter] web:connected response after reconnect:', response);

          if (response.success) {
            // Re-link to daemon (will also request sessions)
            this.linkToDaemonAndRequestSessions();

            // Restart heartbeat monitoring after reconnect
            this.initializeHeartbeatMonitor();
          }
        });
      },
    });

    // Pong event for heartbeat monitoring (self-hosted specific)
    this.socket.on('pong', () => {
      if (this.heartbeatMonitor) {
        this.heartbeatMonitor.handlePong();
      }
    });

    // Daemon events
    this.socket.on('daemon:connected', (data: { deviceId?: string }) => {
      console.log('[SelfHostedAdapter] Daemon connected:', data.deviceId);
      this.daemonStatus = 'running';
      this.emit('daemon:status', {
        deviceId: data.deviceId || '',
        status: 'running',
        sessionCount: 0,
      });
    });

    this.socket.on('daemon:disconnected', (data: { reason?: string }) => {
      console.log('[SelfHostedAdapter] Daemon disconnected:', data.reason);
      this.daemonStatus = 'stopped';
      this.emit('daemon:stopped', data.reason);

      // Clear all sessions when daemon disconnects
      this.activeSessions.clear();
      this.messageCache.clear();
    });

    this.socket.on('daemon:status', (data: { status?: string; sessionCount?: number }) => {
      const status = this.normalizeDaemonStatus(data.status);
      this.daemonStatus = status;
      this.emit('daemon:status', {
        deviceId: '',
        status,
        sessionCount: data.sessionCount || 0,
      });
    });

    // Daemon log events (for parent control command feedback)
    this.socket.on('daemon:log', (data: {
      level?: 'info' | 'warn' | 'error' | 'success';
      message?: string;
      timestamp?: string;
      context?: Record<string, unknown>;
      deviceId?: string;
    }) => {
      console.log('[SelfHostedAdapter] Daemon log:', data.level, data.message, data.context);

      const logEntry = {
        level: (data.level || 'info') as 'info' | 'warn' | 'error' | 'success',
        message: data.message || '',
        timestamp: data.timestamp || new Date().toISOString(),
        context: data.context as {
          operation?: string;
          sessionId?: string;
          sessionCount?: number;
          terminatedCount?: number;
          restartedCount?: number;
          failedCount?: number;
          [key: string]: unknown;
        },
        deviceId: data.deviceId,
      };

      // Add to store for UI display
      useHappyStore.getState().addDaemonLog(logEntry);

      // Also emit event for any listeners
      this.emit('daemon:log', logEntry);
    });

    // Session events
    this.socket.on('session:list', (data: { sessions?: unknown[] }) => {
      console.log('[SelfHostedAdapter] Session list received:', data.sessions?.length);
      const sessions = data.sessions || [];
      const normalized: NormalizedSession[] = [];

      for (const raw of sessions) {
        const session = this.normalizeRawSession(raw);
        if (session) {
          this.activeSessions.set(session.sessionId, session);
          normalized.push(session);
        }
      }

      this.emit('session:list', normalized);
    });

    this.socket.on('session:created', (data: unknown) => {
      this.handleRawSession(data);
    });

    this.socket.on('session:updated', (data: { session_id?: string; sessionId?: string; updates?: unknown }) => {
      const sessionId = data.sessionId || data.session_id;
      if (sessionId && data.updates) {
        this.updateSession(sessionId, data.updates as Partial<NormalizedSession>);
      }
    });

    this.socket.on('session:terminated', (data: { session_id?: string; sessionId?: string; reason?: string }) => {
      const sessionId = data.sessionId || data.session_id;
      if (sessionId) {
        // Mark all tool calls as completed when session terminates (Phase 2 Agent Card Integration)
        useHappyStore.getState().markToolCallsCompleted(sessionId);
        this.removeSession(sessionId, data.reason);
      }
    });

    // Session state change events (real-time state sync from daemon)
    this.socket.on('session:state', (data: {
      sessionId?: string;
      session_id?: string;
      state?: string;
      previousState?: string;
      changeType?: string;
      timestamp?: number;
      lastActivity?: number;
      isRunning?: boolean;
      exitCode?: number;
      lastError?: string;
    }) => {
      const sessionId = data.sessionId || data.session_id;
      if (!sessionId) return;

      console.log('[SelfHostedAdapter] Session state change:', {
        sessionId,
        state: data.state,
        previousState: data.previousState,
        changeType: data.changeType,
      });

      // Normalize status for session update
      const normalizedStatus = this.normalizeStatus(data.state);

      // Update session with new state
      this.updateSession(sessionId, {
        status: normalizedStatus,
        lastActivityAt: data.lastActivity
          ? new Date(data.lastActivity * 1000).toISOString()
          : new Date().toISOString(),
      });

      // Track session state for thinking indicator
      // NOTE: thinking:stop is handled by the dedicated socket listener (line ~604)
      // to avoid duplicate emissions. Only emit thinking:start here.
      const currentState = data.state?.toLowerCase();
      if (currentState === 'processing' || currentState === 'thinking') {
        console.log('[SelfHostedAdapter] Session processing, starting thinking indicator');
        this.emit('thinking:start');
      }

      // Emit session:state event for hooks to consume
      this.emit('session:state', {
        sessionId,
        state: data.state,
        previousState: data.previousState,
        changeType: data.changeType || 'status_change',
        timestamp: data.timestamp || Date.now() / 1000,
        lastActivity: data.lastActivity || Date.now() / 1000,
        isRunning: data.isRunning,
        exitCode: data.exitCode,
        lastError: data.lastError,
      });
    });

    // Message events
    // NOTE: thinking:stop is NOT emitted here - it's handled by the dedicated
    // socket listener to avoid duplicate emissions (server sends thinking:stop event)
    this.socket.on('message:new', (data: unknown) => {
      if (this.socketId) {
        refreshConnection(this.socketId);
      }

      const rawData = data as Record<string, unknown>;

      // Handle Vibe Remote direct format
      if (rawData.sessionId && rawData.messageId && rawData.content) {
        if (!markEventProcessed(rawData.messageId as string, 'message:new')) {
          return;
        }
        this.handleRawMessageNew(data);
        return;
      }

      // Handle CLI Dashboard wrapped format
      const msg = rawData.message as Record<string, unknown> | undefined;
      if (msg) {
        const messageId = (msg.id || msg.message_id || msg.messageId) as string;
        if (messageId && !markEventProcessed(messageId, 'message:new')) {
          return;
        }
        this.handleRawMessageNew(data);
      }
    });

    this.socket.on('message:stream', (data: unknown) => {
      if (this.socketId) {
        refreshConnection(this.socketId);
      }
      this.handleRawStreamChunk(data);
    });

    this.socket.on('messages:history', (data: unknown) => {
      this.handleRawHistory(data);
    });

    // Session output from daemon (primary event for Claude SDK responses)
    // Uses streaming accumulation to combine thinking text with tool output
    this.socket.on('session:output', (data: unknown) => {
      if (this.socketId) {
        refreshConnection(this.socketId);
      }

      const rawData = data as Record<string, unknown>;
      const sessionId = rawData.sessionId as string;
      const content = rawData.content as string;
      const type = rawData.type as string;
      const timestamp = rawData.timestamp as string;
      const isComplete = rawData.isComplete as boolean;

      console.log(`[SelfHostedAdapter] session:output received: type=${type}, contentLen=${content?.length || 0}`);

      // Bridge to stream:frame for terminal view (hybrid mode)
      // This allows the terminal pane to display PTY content from session:output events
      if (content && content.length > 0) {
        // Map session output type to frame type
        const frameType = type === 'assistant' ? 'assistant'
          : type === 'error' ? 'error'
          : type === 'system' ? 'system'
          : 'debug';

        const parsedTimestamp = timestamp ? new Date(timestamp).getTime() : Date.now();

        // CRITICAL: Store to hybridStore for persistence across terminal recreations
        // This ensures live PTY output survives even if terminal is recreated
        const hybridStore = useHybridStore.getState();
        hybridStore.initSession(sessionId);
        hybridStore.handleStreamFrame({
          sessionId,
          timestamp: parsedTimestamp,
          seq: parsedTimestamp,
          content,
          frameType,
          isFinal: isComplete || type === 'response_complete',
        });

        // Also emit event for any active listeners
        this.emit('stream:frame', {
          sessionId,
          timestamp: parsedTimestamp,
          content,
          frameType,
          isFinal: isComplete || type === 'response_complete',
        });
      }

      // Handle assistant messages with streaming accumulation
      // This preserves "thinking" text between tool calls in a single message
      if (type === 'assistant' && content && content.trim().length > 0) {
        let stream = this.activeStreams.get(sessionId);

        if (!stream) {
          // Create new streaming message
          const messageId = `sdk-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          stream = {
            messageId,
            content: '',
            startTime: Date.now(),
          };
          this.activeStreams.set(sessionId, stream);

          // Emit initial streaming message
          this.emit('message:new', {
            id: messageId,
            sessionId,
            content,
            role: 'assistant',
            timestamp: timestamp || new Date().toISOString(),
            isComplete: false, // Streaming - not complete yet
          });
          stream.content = content;
          console.log(`[SelfHostedAdapter] Started streaming message: ${messageId}`);
        } else {
          // Append to existing streaming message
          // Add separator between chunks for readability
          const separator = stream.content.endsWith('\n') ? '' : '\n';
          stream.content += separator + content;

          // Update the message with accumulated content
          this.emit('message:update', {
            id: stream.messageId,
            sessionId,
            content: stream.content,
            isComplete: false,
          });
          console.log(`[SelfHostedAdapter] Appended to streaming message: ${stream.messageId}, totalLen=${stream.content.length}`);
        }
      }

      // Handle response complete (turn finished) - finalize accumulated message
      if (type === 'response_complete') {
        const stream = this.activeStreams.get(sessionId);
        if (stream) {
          // Finalize the streaming message
          this.emit('message:update', {
            id: stream.messageId,
            sessionId,
            content: stream.content,
            isComplete: true,
          });
          console.log(`[SelfHostedAdapter] Finalized streaming message: ${stream.messageId}, totalLen=${stream.content.length}`);
          this.activeStreams.delete(sessionId);
        }
        this.emit('thinking:stop');
      }

      // Handle system messages (these don't stream)
      if (type === 'system' && content) {
        const messageId = `sys-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        this.emit('message:new', {
          id: messageId,
          sessionId,
          content,
          role: 'system',
          timestamp: timestamp || new Date().toISOString(),
          isComplete: true,
        });
      }
    });

    // Session replay data (sent when joining a session room to catch up on missed output)
    // This fixes the race condition where output is broadcast before client joins
    this.socket.on('session:replay', (data: {
      sessionId: string;
      frames?: Array<{ content: string; frameType: string; timestamp: string; seq: number }>;
      accumulatedContent?: string;
      frameCount?: number;
    }) => {
      console.log(`[SelfHostedAdapter] session:replay received: frames=${data.frames?.length || 0}, accumulatedLen=${data.accumulatedContent?.length || 0}`);

      const sessionId = data.sessionId;

      // CRITICAL: Store to hybridStore FIRST, before emitting event
      // This ensures terminal content survives even if no listener is subscribed yet
      // (fixes race condition where terminal is recreated after replay is received)
      const hybridStore = useHybridStore.getState();
      hybridStore.initSession(sessionId);

      // If we have accumulated content, emit it as a single stream:frame
      // This is more efficient than emitting individual frames
      if (data.accumulatedContent && data.accumulatedContent.length > 0) {
        // Store to hybridStore for persistence across terminal recreations
        hybridStore.handleStreamFrame({
          sessionId,
          timestamp: Date.now(),
          seq: Date.now(),
          content: data.accumulatedContent,
          frameType: 'assistant',
          isFinal: true,
        });
        console.log(`[SelfHostedAdapter] Stored replay to hybridStore, len=${data.accumulatedContent.length}`);

        // Also emit event for any active listeners
        this.emit('stream:frame', {
          sessionId,
          timestamp: Date.now(),
          content: data.accumulatedContent,
          frameType: 'assistant',
          isFinal: true,
        });
        console.log(`[SelfHostedAdapter] Emitted replay as stream:frame, len=${data.accumulatedContent.length}`);
      }
      // Alternatively, emit individual frames if no accumulated content
      else if (data.frames && data.frames.length > 0) {
        for (const frame of data.frames) {
          // Store each frame to hybridStore
          hybridStore.handleStreamFrame({
            sessionId,
            timestamp: new Date(frame.timestamp).getTime(),
            seq: frame.seq,
            content: frame.content,
            frameType: frame.frameType as 'assistant' | 'error' | 'system' | 'debug',
            isFinal: false,
          });

          // Also emit event
          this.emit('stream:frame', {
            sessionId,
            timestamp: new Date(frame.timestamp).getTime(),
            content: frame.content,
            frameType: frame.frameType as 'assistant' | 'error' | 'system' | 'debug',
            isFinal: false,
          });
        }
        console.log(`[SelfHostedAdapter] Stored and emitted ${data.frames.length} replay frames`);
      }
    });

    // Thinking events
    this.socket.on('thinking:start', () => {
      this.emit('thinking:start');
    });

    this.socket.on('thinking:stop', () => {
      this.emit('thinking:stop');
    });

    // Auth events
    this.socket.on('auth:required', () => {
      this.emit('auth:required');
    });

    this.socket.on('auth:success', (data: { deviceId?: string }) => {
      this.emit('auth:success', data.deviceId || '');
    });

    this.socket.on('auth:error', (data: { error?: string; message?: string }) => {
      this.emit('auth:error', data.error || data.message || 'Authentication failed');
    });

    // Pairing events
    this.socket.on('pairing:code', (data: { code?: string; expiresIn?: number; expiresAt?: string }) => {
      if (data.code) {
        this.emit('pairing:code', {
          code: data.code,
          expiresIn: data.expiresIn || 300,
          expiresAt: data.expiresAt ? new Date(data.expiresAt) : new Date(Date.now() + 300000),
        });
      }
    });

    this.socket.on('pairing:success', (data: { accessToken?: string; refreshToken?: string; expiresIn?: number }) => {
      if (data.accessToken) {
        this.authToken = data.accessToken;
        this.emit('pairing:success', {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || '',
          expiresIn: data.expiresIn || 900,
        });
      }
    });

    this.socket.on('pairing:error', (data: { error?: string; message?: string }) => {
      this.emit('pairing:error', data.error || data.message || 'Pairing failed');
    });

    // Context events
    this.socket.on('context:update', (data: { tokensUsed?: number; tokensMax?: number }) => {
      const tokensUsed = data.tokensUsed || 0;
      const tokensMax = data.tokensMax || 200000;
      const percentageRemaining = tokensMax > 0 ? ((tokensMax - tokensUsed) / tokensMax) * 100 : 0;

      this.emit('context:update', {
        tokensUsed,
        tokensMax,
        percentageRemaining,
      });
    });

    // Tool events
    this.socket.on('tool:output', (data: { sessionId?: string; session_id?: string; type?: string; metadata?: Record<string, unknown> }) => {
      const sessionId = data.sessionId || data.session_id;
      if (sessionId && data.type) {
        this.emit('tool:output', sessionId, data.type, data.metadata);
      }
    });

    // ==========================================================
    // Visual Parity Events (P2-T6)
    // ==========================================================

    // Tool lifecycle events
    this.socket.on('tool:start', (data: {
      sessionId: string;
      toolId: string;
      toolName: string;
      startTime: number;
      args?: Record<string, unknown>;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool started:', data.toolName, data.toolId);

      // Wire to store for tool call aggregation (Phase 2 Agent Card Integration)
      useHappyStore.getState().handleToolStart({
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: data.toolName,
        startTime: data.startTime,
        args: data.args,
      });

      this.emit('tool:start', {
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: data.toolName,
        startTime: data.startTime,
        args: data.args,
        seq: data.seq,
      });
    });

    this.socket.on('tool:end', (data: {
      sessionId: string;
      toolId: string;
      toolName: string;
      endTime: number;
      durationMs: number;
      success: boolean;
      error?: string;
      result?: unknown;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool ended:', data.toolName, `${data.durationMs}ms`, data.success ? 'success' : 'failed');

      // Wire to store for tool call aggregation (Phase 2 Agent Card Integration)
      useHappyStore.getState().handleToolEnd({
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: data.toolName,
        endTime: data.endTime,
        durationMs: data.durationMs,
        success: data.success,
        error: data.error,
        result: typeof data.result === 'string' ? data.result : JSON.stringify(data.result),
      });

      this.emit('tool:end', {
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: data.toolName,
        endTime: data.endTime,
        durationMs: data.durationMs,
        success: data.success,
        error: data.error,
        result: data.result,
        seq: data.seq,
      });
    });

    // Todo list events
    this.socket.on('todo:update', (data: {
      sessionId: string;
      todos: Array<{
        id: string;
        content: string;
        status: 'pending' | 'in_progress' | 'completed';
        activeForm?: string;
        order: number;
      }>;
      timestamp: number;
      changeType: 'add' | 'update' | 'remove' | 'reorder' | 'full_sync';
      changedIds: string[];
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Todo update:', data.todos.length, 'items', data.changeType);
      this.emit('todo:update', {
        sessionId: data.sessionId,
        todos: data.todos,
        timestamp: data.timestamp,
        changeType: data.changeType,
        changedIds: data.changedIds,
        seq: data.seq,
      });
    });

    // Agent lifecycle events
    this.socket.on('agent:spawn', (data: {
      sessionId: string;
      agentId: string;
      spawnTime: number;
      parentAgentId?: string;
      taskDescription?: string;
      agentName?: string;
      estimatedTokens?: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Agent spawned:', data.agentId, data.agentName, data.taskDescription?.slice(0, 50));

      // Wire to store for LiveAgentCard integration (Phase 2 Agent Card Integration)
      useHappyStore.getState().handleAgentSpawn({
        sessionId: data.sessionId,
        agentId: data.agentId,
        spawnTime: data.spawnTime,
        parentAgentId: data.parentAgentId,
        taskDescription: data.taskDescription,
        agentName: data.agentName,
        estimatedTokens: data.estimatedTokens,
      });

      this.emit('agent:spawn', {
        sessionId: data.sessionId,
        agentId: data.agentId,
        spawnTime: data.spawnTime,
        parentAgentId: data.parentAgentId,
        taskDescription: data.taskDescription,
        agentName: data.agentName,
        estimatedTokens: data.estimatedTokens,
        seq: data.seq,
      });
    });

    this.socket.on('agent:update', (data: {
      sessionId: string;
      agentId: string;
      timestamp: number;
      status?: string;
      tokensUsed?: number;
      progressPercentage?: number;
      currentAction?: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Agent update:', data.agentId, data.status);

      // Wire to store for LiveAgentCard integration (Phase 2 Agent Card Integration)
      useHappyStore.getState().handleAgentUpdate({
        sessionId: data.sessionId,
        agentId: data.agentId,
        tokensUsed: data.tokensUsed,
        toolCount: undefined, // Updated via tool:end events
      });

      this.emit('agent:update', {
        sessionId: data.sessionId,
        agentId: data.agentId,
        timestamp: data.timestamp,
        status: data.status,
        tokensUsed: data.tokensUsed,
        progressPercentage: data.progressPercentage,
        currentAction: data.currentAction,
        seq: data.seq,
      });
    });

    this.socket.on('agent:complete', (data: {
      sessionId: string;
      agentId: string;
      endTime: number;
      durationMs: number;
      success: boolean;
      tokensUsed: number;
      error?: string;
      result?: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Agent complete:', data.agentId, data.success ? 'success' : 'failed', `${data.durationMs}ms`);

      // Wire to store for LiveAgentCard integration (Phase 2 Agent Card Integration)
      useHappyStore.getState().handleAgentComplete({
        sessionId: data.sessionId,
        agentId: data.agentId,
        endTime: data.endTime,
        success: data.success,
        tokensUsed: data.tokensUsed,
        toolCount: undefined, // Final count from aggregation
      });

      this.emit('agent:complete', {
        sessionId: data.sessionId,
        agentId: data.agentId,
        endTime: data.endTime,
        durationMs: data.durationMs,
        success: data.success,
        tokensUsed: data.tokensUsed,
        error: data.error,
        result: data.result,
        seq: data.seq,
      });
    });

    // Progress events
    this.socket.on('progress:update', (data: {
      sessionId: string;
      operationType: string;
      progressPercentage: number;
      currentItem?: string;
      totalItems?: number;
      completedItems?: number;
      estimatedRemainingMs?: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Progress:', data.operationType, `${data.progressPercentage}%`);
      this.emit('progress:update', {
        sessionId: data.sessionId,
        operationType: data.operationType,
        progressPercentage: data.progressPercentage,
        currentItem: data.currentItem,
        totalItems: data.totalItems,
        completedItems: data.completedItems,
        estimatedRemainingMs: data.estimatedRemainingMs,
        seq: data.seq,
      });
    });

    // Spinner events
    this.socket.on('spinner:start', (data: {
      sessionId: string;
      spinnerId: string;
      message: string;
      startTime: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Spinner start:', data.spinnerId, data.message);
      this.emit('spinner:start', {
        sessionId: data.sessionId,
        spinnerId: data.spinnerId,
        message: data.message,
        startTime: data.startTime,
        seq: data.seq,
      });
    });

    this.socket.on('spinner:stop', (data: {
      sessionId: string;
      spinnerId: string;
      endTime: number;
      success: boolean;
      finalMessage?: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Spinner stop:', data.spinnerId, data.success ? 'success' : 'failed');
      this.emit('spinner:stop', {
        sessionId: data.sessionId,
        spinnerId: data.spinnerId,
        endTime: data.endTime,
        success: data.success,
        finalMessage: data.finalMessage,
        seq: data.seq,
      });
    });

    // ==========================================================
    // End Visual Parity Events
    // ==========================================================

    // ==========================================================
    // Hybrid Events (stream:*, visual:*, state:*)
    // Phase: Hybrid Orchestrator Migration
    // ==========================================================

    // Stream events - PTY terminal output
    this.socket.on('stream:frame', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      content: string;
      frameType: 'assistant' | 'system' | 'error' | 'debug';
      isFinal: boolean;
      tokenCount?: number;
    }) => {
      console.log('[SelfHostedAdapter] Stream frame:', data.frameType, data.content.length, 'chars');
      this.emit('stream:frame', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        content: data.content,
        frameType: data.frameType,
        isFinal: data.isFinal,
        tokenCount: data.tokenCount,
      });
    });

    this.socket.on('stream:chunk', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      content: string;
      chunkIndex: number;
      totalChunks?: number;
      isFinal: boolean;
    }) => {
      console.log('[SelfHostedAdapter] Stream chunk:', data.chunkIndex, '/', data.totalChunks || '?');
      this.emit('stream:chunk', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        content: data.content,
        chunkIndex: data.chunkIndex,
        totalChunks: data.totalChunks,
        isFinal: data.isFinal,
      });
    });

    // Visual hybrid events (namespaced versions of existing events)
    this.socket.on('visual:tool_start', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      toolId: string;
      toolName: string;
      operation?: string;
      resource?: string;
      preview?: string;
    }) => {
      console.log('[SelfHostedAdapter] Visual tool start:', data.toolName, data.toolId);

      // Wire to store for tool call aggregation
      useHappyStore.getState().handleToolStart({
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: data.toolName,
        startTime: data.timestamp * 1000,
        args: data.preview ? { preview: data.preview } : undefined,
      });

      this.emit('visual:tool_start', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        toolId: data.toolId,
        toolName: data.toolName,
        operation: data.operation,
        resource: data.resource,
        preview: data.preview,
      });
    });

    this.socket.on('visual:tool_end', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      toolId: string;
      success: boolean;
      result?: string;
      error?: string;
      durationMs?: number;
    }) => {
      console.log('[SelfHostedAdapter] Visual tool end:', data.toolId, data.success ? 'success' : 'failed');

      // Wire to store for tool call aggregation
      useHappyStore.getState().handleToolEnd({
        sessionId: data.sessionId,
        toolId: data.toolId,
        toolName: '', // Not provided in hybrid event
        endTime: data.timestamp * 1000,
        durationMs: data.durationMs || 0,
        success: data.success,
        error: data.error,
        result: data.result,
      });

      this.emit('visual:tool_end', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        toolId: data.toolId,
        success: data.success,
        result: data.result,
        error: data.error,
        durationMs: data.durationMs,
      });
    });

    this.socket.on('visual:agent_spawn', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      agentId: string;
      agentType: string;
      description?: string;
      parentAgentId?: string;
    }) => {
      console.log('[SelfHostedAdapter] Visual agent spawn:', data.agentId, data.agentType);

      // Wire to store for LiveAgentCard integration
      useHappyStore.getState().handleAgentSpawn({
        sessionId: data.sessionId,
        agentId: data.agentId,
        spawnTime: data.timestamp * 1000,
        parentAgentId: data.parentAgentId,
        taskDescription: data.description,
        agentName: data.agentType,
      });

      this.emit('visual:agent_spawn', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        agentId: data.agentId,
        agentType: data.agentType,
        description: data.description,
        parentAgentId: data.parentAgentId,
      });
    });

    this.socket.on('visual:agent_update', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      agentId: string;
      status?: string;
      progress?: number;
      currentTask?: string;
    }) => {
      console.log('[SelfHostedAdapter] Visual agent update:', data.agentId, data.status);

      // Wire to store for LiveAgentCard integration
      useHappyStore.getState().handleAgentUpdate({
        sessionId: data.sessionId,
        agentId: data.agentId,
        tokensUsed: undefined,
        toolCount: undefined,
      });

      this.emit('visual:agent_update', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        agentId: data.agentId,
        status: data.status,
        progress: data.progress,
        currentTask: data.currentTask,
      });
    });

    this.socket.on('visual:agent_complete', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      agentId: string;
      success: boolean;
      result?: string;
      error?: string;
      durationMs?: number;
    }) => {
      console.log('[SelfHostedAdapter] Visual agent complete:', data.agentId, data.success ? 'success' : 'failed');

      // Wire to store for LiveAgentCard integration
      useHappyStore.getState().handleAgentComplete({
        sessionId: data.sessionId,
        agentId: data.agentId,
        endTime: data.timestamp * 1000,
        success: data.success,
        tokensUsed: 0,
        toolCount: undefined,
      });

      this.emit('visual:agent_complete', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        agentId: data.agentId,
        success: data.success,
        result: data.result,
        error: data.error,
        durationMs: data.durationMs,
      });
    });

    this.socket.on('visual:permission_request', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      permissionId: string;
      toolId?: string;
      toolName?: string;
      permissionType: string;
      resource: string;
      description?: string;
      preview?: string;
      riskLevel?: string;
      timeoutMs?: number;
      remainingMs?: number;
    }) => {
      console.log('[SelfHostedAdapter] Visual permission request:', data.permissionId, data.permissionType);
      this.emit('visual:permission_request', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        permissionId: data.permissionId,
        toolId: data.toolId,
        toolName: data.toolName,
        permissionType: data.permissionType,
        resource: data.resource,
        description: data.description,
        preview: data.preview,
        riskLevel: data.riskLevel,
        timeoutMs: data.timeoutMs,
        remainingMs: data.remainingMs,
      });
    });

    this.socket.on('visual:progress', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      progressId?: string;
      current: number;
      total: number;
      message?: string;
      phase?: string;
    }) => {
      console.log('[SelfHostedAdapter] Visual progress:', data.current, '/', data.total, data.message);
      this.emit('visual:progress', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        progressId: data.progressId,
        current: data.current,
        total: data.total,
        message: data.message,
        phase: data.phase,
      });
    });

    this.socket.on('visual:attachment_status', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      status: {
        attachmentId: string;
        name: string;
        status: 'uploading' | 'processing' | 'ready' | 'failed';
        progress?: number;
        error?: string;
      };
    }) => {
      console.log('[SelfHostedAdapter] Visual attachment status:', data.status.attachmentId, data.status.status);
      this.emit('visual:attachment_status', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        status: data.status,
      });
    });

    // State events - session state transitions
    this.socket.on('state:update', (data: {
      sessionId: string;
      timestamp: number;
      seq?: number;
      state: 'idle' | 'thinking' | 'tooling' | 'waiting_for_user' | 'done' | 'error';
      previousState?: string;
      reason?: string;
      metadata?: Record<string, unknown>;
    }) => {
      console.log('[SelfHostedAdapter] State update:', data.state, data.previousState ? `from ${data.previousState}` : '');
      this.emit('state:update', {
        sessionId: data.sessionId,
        timestamp: data.timestamp,
        seq: data.seq,
        state: data.state,
        previousState: data.previousState,
        reason: data.reason,
        metadata: data.metadata,
      });
    });

    // ==========================================================
    // End Hybrid Events
    // ==========================================================

    // Title generation events (AI-powered via daemon's Claude SDK)
    this.socket.on('title:generated', (data: {
      title?: string;
      aiGenerated?: boolean;
      sessionId?: string;
      requestId?: string;
      error?: string;
    }) => {
      console.log('[SelfHostedAdapter] Title generated:', {
        title: data.title,
        aiGenerated: data.aiGenerated,
        sessionId: data.sessionId,
      });

      // Emit event for store to consume
      this.emit('title:generated', {
        title: data.title || 'New Session',
        aiGenerated: data.aiGenerated || false,
        sessionId: data.sessionId,
        requestId: data.requestId,
        error: data.error,
      });
    });

    // Server-side session sync events
    this.socket.on('sync:sessions', (data: { sessions?: unknown[]; daemonId?: string; timestamp?: string }) => {
      console.log('[SelfHostedAdapter] Sync sessions received:', {
        count: data.sessions?.length,
        daemonId: data.daemonId,
      });

      if (data.sessions && data.daemonId) {
        this.emit('sync:sessions', data.sessions as SyncedSession[], data.daemonId);
      }
    });

    this.socket.on('sync:session:updated', (data: { session?: unknown; daemonId?: string; timestamp?: string }) => {
      console.log('[SelfHostedAdapter] Session updated from server:', {
        daemonId: data.daemonId,
      });

      if (data.session && data.daemonId) {
        this.emit('sync:session:updated', data.session as SyncedSession, data.daemonId);
      }
    });

    this.socket.on('sync:session:removed', (data: { sessionId?: string; daemonId?: string; timestamp?: string }) => {
      console.log('[SelfHostedAdapter] Session removed from server:', {
        sessionId: data.sessionId,
        daemonId: data.daemonId,
      });

      if (data.sessionId && data.daemonId) {
        this.emit('sync:session:removed', data.sessionId, data.daemonId);
      }
    });

    // Session title update events (real-time sync from relay server)
    this.socket.on('session:title:updated', (data: {
      session_id: string;
      title: string;
      updated_by: string;
      timestamp: string;
    }) => {
      console.log('[SelfHostedAdapter] Session title updated:', {
        sessionId: data.session_id,
        title: data.title,
        updatedBy: data.updated_by,
      });

      // Update local session cache
      this.updateSession(data.session_id, { title: data.title });

      // Emit event for happyStore to handle
      this.emit('session:title:updated', data);
    });

    // Session lock status update events (real-time sync from relay server)
    this.socket.on('session:lock:updated', (data: {
      session_id: string;
      title_locked: boolean;
      updated_by: string;
      timestamp: string;
    }) => {
      console.log('[SelfHostedAdapter] Session lock updated:', {
        sessionId: data.session_id,
        titleLocked: data.title_locked,
        updatedBy: data.updated_by,
      });

      // Emit event for happyStore to handle
      // Note: NormalizedSession doesn't have titleLocked field, so we only emit
      this.emit('session:lock:updated', data);
    });

    // ==========================================================
    // TSRP Events (Tool Stream Resolution Protocol)
    // Session Rehydration Protocol (SRP) + Tool Lifecycle
    // ==========================================================

    // Session rehydration response (page refresh recovery)
    this.socket.on('session:rehydrate:response', (data: {
      success: boolean;
      sessionId: string;
      lastEventId: number;
      session?: {
        sessionId: string;
        title?: string;
        status?: string;
        createdAt?: string;
        lastActivityAt?: string;
      };
      messages?: Array<{
        id: string;
        role: string;
        content: string;
        timestamp: string;
      }>;
      toolCalls?: Array<{
        id: string;
        toolName: string;
        status: string;
        parentMessageId?: string;
        startedAt?: string;
        endedAt?: string;
        durationMs?: number;
      }>;
      activeRun?: {
        id: string;
        status: string;
        startedAt: string;
      };
      error?: string;
    }) => {
      console.log('[SelfHostedAdapter] Session rehydration response:', {
        success: data.success,
        sessionId: data.sessionId,
        lastEventId: data.lastEventId,
        messagesCount: data.messages?.length,
        toolCallsCount: data.toolCalls?.length,
        hasActiveRun: !!data.activeRun,
      });

      this.emit('session:rehydrated', data);
    });

    // Incremental event replay response
    this.socket.on('session:events:data', (data: {
      success: boolean;
      sessionId: string;
      events: Array<{
        id: number;
        eventType: string;
        payload: Record<string, unknown>;
        createdAt: string;
      }>;
      lastEventId: number;
      hasMore: boolean;
    }) => {
      console.log('[SelfHostedAdapter] Event replay data:', {
        sessionId: data.sessionId,
        eventsCount: data.events?.length,
        lastEventId: data.lastEventId,
        hasMore: data.hasMore,
      });

      this.emit('session:events:replay', data);
    });

    // Run lifecycle events
    this.socket.on('run:started', (data: {
      sessionId: string;
      runId: string;
      startedAt: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Run started:', data.runId, 'in session', data.sessionId);
      this.emit('run:started', data);
    });

    this.socket.on('run:completed', (data: {
      sessionId: string;
      runId: string;
      endedAt: string;
      durationMs?: number;
      toolsClosedCount?: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Run completed:', data.runId, 'tools closed:', data.toolsClosedCount);
      // When run completes, mark all tool calls as completed (D1 heuristic confirmation)
      useHappyStore.getState().markToolCallsCompleted(data.sessionId);
      this.emit('run:completed', data);
    });

    this.socket.on('run:failed', (data: {
      sessionId: string;
      runId: string;
      endedAt: string;
      error?: string;
      toolsClosedCount?: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Run failed:', data.runId, 'error:', data.error);
      useHappyStore.getState().markToolCallsCompleted(data.sessionId);
      this.emit('run:failed', data);
    });

    this.socket.on('run:canceled', (data: {
      sessionId: string;
      runId: string;
      endedAt: string;
      reason?: string;
      toolsClosedCount?: number;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Run canceled:', data.runId, 'reason:', data.reason);
      useHappyStore.getState().markToolCallsCompleted(data.sessionId);
      this.emit('run:canceled', data);
    });

    // Tool lifecycle events (TSRP - enhanced with lineage)
    this.socket.on('tool:created', (data: {
      sessionId: string;
      toolCallId: string;
      toolName: string;
      parentMessageId: string;
      runId?: string;
      input?: Record<string, unknown>;
      createdAt: string;
      status: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool created:', data.toolName, 'parent:', data.parentMessageId);

      // Create tool call with lineage info
      useHappyStore.getState().handleToolStart({
        sessionId: data.sessionId,
        toolId: data.toolCallId,
        toolName: data.toolName,
        startTime: new Date(data.createdAt).getTime(),
        args: data.input,
        parentMessageId: data.parentMessageId,
        runId: data.runId,
      });

      this.emit('tool:created', data);
    });

    this.socket.on('tool:completed', (data: {
      sessionId: string;
      toolCallId: string;
      toolName: string;
      endedAt: string;
      durationMs: number;
      output?: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool completed:', data.toolName, `${data.durationMs}ms`);

      useHappyStore.getState().handleToolEnd({
        sessionId: data.sessionId,
        toolId: data.toolCallId,
        toolName: data.toolName,
        endTime: new Date(data.endedAt).getTime(),
        durationMs: data.durationMs,
        success: true,
        result: data.output,
      });

      this.emit('tool:completed', data);
    });

    this.socket.on('tool:failed', (data: {
      sessionId: string;
      toolCallId: string;
      toolName: string;
      endedAt: string;
      durationMs: number;
      error: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool failed:', data.toolName, 'error:', data.error);

      useHappyStore.getState().handleToolEnd({
        sessionId: data.sessionId,
        toolId: data.toolCallId,
        toolName: data.toolName,
        endTime: new Date(data.endedAt).getTime(),
        durationMs: data.durationMs,
        success: false,
        error: data.error,
      });

      this.emit('tool:failed', data);
    });

    this.socket.on('tool:canceled', (data: {
      sessionId: string;
      toolCallId: string;
      toolName: string;
      endedAt: string;
      durationMs?: number;
      reason?: string;
      seq?: number;
    }) => {
      console.log('[SelfHostedAdapter] Tool canceled:', data.toolName, 'reason:', data.reason);

      useHappyStore.getState().handleToolEnd({
        sessionId: data.sessionId,
        toolId: data.toolCallId,
        toolName: data.toolName,
        endTime: new Date(data.endedAt).getTime(),
        durationMs: data.durationMs || 0,
        success: false,
        error: data.reason || 'canceled',
      });

      this.emit('tool:canceled', data);
    });

    // Session stopped (all tools/runs canceled by user)
    this.socket.on('session:stopped', (data: {
      sessionId: string;
      runsCanceled: number;
      toolsCanceled: number;
      stoppedAt: string;
      reason?: string;
    }) => {
      console.log('[SelfHostedAdapter] Session stopped:', {
        sessionId: data.sessionId,
        runsCanceled: data.runsCanceled,
        toolsCanceled: data.toolsCanceled,
      });

      // Mark all tool calls as completed
      useHappyStore.getState().markToolCallsCompleted(data.sessionId);
      this.emit('session:stopped', data);
    });
  }

  private normalizeRawSession(raw: unknown): NormalizedSession | null {
    const data = raw as Record<string, unknown>;
    const sessionId = (data.sessionId || data.session_id || data.id) as string;

    if (!sessionId) return null;

    return {
      sessionId,
      status: this.normalizeStatus(data.status as string || data.state as string),
      title: (data.title || data.session_name) as string | undefined,
      createdAt: (data.createdAt || data.created_at || new Date().toISOString()) as string,
      lastActivityAt: (data.lastActivityAt || data.last_activity_at || new Date().toISOString()) as string,
      messageCount: (data.messageCount ?? data.message_count ?? 0) as number,
      unreadCount: (data.unreadCount ?? data.unread_count ?? 0) as number,
      error: data.error as string | undefined,
    };
  }

  /**
   * Normalize daemon status to UI session status
   *
   * Status mapping:
   * - idle/waiting_for_input -> active (ready for user input)
   * - processing/thinking -> processing (Claude is thinking)
   * - tool_executing/waiting -> executing (tool running)
   * - terminated/stopped -> terminated
   * - crashed/error -> crashed
   * - disconnected -> disconnected
   * - paused/suspended -> paused
   */
  private normalizeStatus(status?: string): NormalizedSession['status'] {
    if (!status) return 'active'; // Default to active (ready for input)
    const lower = status.toLowerCase();

    // Ready for input states -> active
    if (lower === 'idle' || lower === 'waiting_for_input') return 'active';

    // Running/active states (session connected and operational)
    if (lower === 'active' || lower === 'running') return 'active';

    // Processing states -> processing (Claude thinking)
    if (lower === 'processing' || lower === 'thinking') return 'processing';

    // Tool execution states -> executing
    if (lower === 'tool_executing' || lower === 'waiting') return 'executing';

    // Session lifecycle states
    if (lower === 'paused' || lower === 'suspended') return 'paused';
    if (lower === 'terminated' || lower === 'stopped') return 'terminated';
    if (lower === 'crashed' || lower === 'error') return 'crashed';
    if (lower === 'disconnected') return 'disconnected';

    // Default to active for unknown states
    return 'active';
  }

  private normalizeDaemonStatus(status?: string): DaemonStatus {
    if (!status) return 'unknown';
    const lower = status.toLowerCase();
    // 'connected' is emitted when we successfully link to a daemon
    if (lower === 'running' || lower === 'active' || lower === 'connected') return 'running';
    if (lower === 'stopping') return 'stopping';
    if (lower === 'stopped' || lower === 'inactive') return 'stopped';
    if (lower === 'disconnected') return 'stopped';
    if (lower === 'reconnecting') return 'stopping';
    return 'unknown';
  }

  // ============================================
  // Session Operations
  // ============================================

  async createSession(options?: SessionOptions): Promise<string | null> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot create session - not connected');
      return null;
    }

    return new Promise((resolve) => {
      let resolved = false;

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.error('[SelfHostedAdapter] Create session timeout');
          cleanup();
          resolve(null);
        }
      }, 30000);

      // Listen for session:created event from daemon (via relay)
      const onSessionCreated = (data: { sessionId?: string; session_id?: string }) => {
        if (!resolved) {
          const sessionId = data.sessionId || data.session_id;
          if (sessionId) {
            resolved = true;
            console.log('[SelfHostedAdapter] Session created via event:', sessionId);
            cleanup();
            resolve(sessionId);
          }
        }
      };

      // Listen for session:error event
      const onSessionError = (data: { error?: string; code?: string }) => {
        if (!resolved) {
          resolved = true;
          console.error('[SelfHostedAdapter] Session creation error:', data.error);
          cleanup();
          resolve(null);
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.socket?.off('session:created', onSessionCreated);
        this.socket?.off('session:error', onSessionError);
      };

      // Register event listeners
      this.socket!.on('session:created', onSessionCreated);
      this.socket!.on('session:error', onSessionError);

      // Emit create request
      // If usePty is true, pass use_sdk: false to create a PTY session (full CLI experience)
      const useSdk = options?.usePty === true ? false : undefined;
      console.log('[SelfHostedAdapter] Emitting session:create with deviceId:', this.linkedDaemonDeviceId, 'usePty:', options?.usePty);
      this.socket!.emit('session:create', {
        deviceId: this.linkedDaemonDeviceId || options?.daemonId,
        name: options?.name,
        args: options?.args,
        cwd: options?.cwd,
        workingDirectory: options?.cwd,
        env: options?.env,
        use_sdk: useSdk,  // false for PTY sessions, undefined for default (SDK)
      }, (response: { success?: boolean; error?: string; message?: string }) => {
        // Relay just acknowledges forwarding, not the actual session creation
        if (response.success === false) {
          if (!resolved) {
            resolved = true;
            console.error('[SelfHostedAdapter] Create session failed to forward:', response.error);
            cleanup();
            resolve(null);
          }
        } else {
          console.log('[SelfHostedAdapter] Request forwarded to daemon, waiting for session:created...');
        }
      });
    });
  }

  joinSession(sessionId: string): void {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot join session - not connected');
      return;
    }

    if (!markSessionJoined(sessionId)) {
      console.log('[SelfHostedAdapter] Session already joined:', sessionId);
      return;
    }

    console.log('[SelfHostedAdapter] Joining session:', sessionId);
    this.socket.emit('session:join', { session_id: sessionId, sessionId });

    // SRP: Persist session ID to localStorage for rehydration on refresh
    this.persistCurrentSession(sessionId);

    // FIX: Fetch message history when joining a session
    // This ensures history is loaded when switching between sessions
    this.requestMessageHistory(sessionId);
  }

  leaveSession(sessionId: string): void {
    if (!this.socket?.connected) return;

    markSessionLeft(sessionId);
    this.socket.emit('session:leave', { session_id: sessionId, sessionId });
  }

  terminateSession(sessionId: string): void {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot terminate session - not connected');
      return;
    }

    markSessionLeft(sessionId);
    console.log('[SelfHostedAdapter] Terminating session:', sessionId);
    this.socket.emit('session:terminate', { session_id: sessionId, sessionId });
  }

  /**
   * Archive (delete) a session - removes from UI and notifies server
   * BUG-001 FIX: Implement archiveSession for self-hosted mode
   * BUG-002 FIX: localStorage removed - sessions are server-managed only
   * BUG-003 FIX: Use session:delete to delete from both server DB AND daemon memory
   *              This prevents "resurrection bug" where sessions reappear after refresh
   */
  override async archiveSession(
    sessionId: string
  ): Promise<{ success: boolean; error?: string }> {
    console.log('[SelfHostedAdapter] Deleting session:', sessionId);

    // Remove from local state immediately for instant UI feedback
    this.activeSessions.delete(sessionId);
    this.messageCache.delete(sessionId);
    markSessionLeft(sessionId);

    // SRP: Clear persisted session if this was the currently selected one
    if (this.getPersistedSession() === sessionId) {
      this.clearPersistedSession();
    }

    // Emit removal event to update UI
    this.emit('session:terminated', sessionId, 'deleted');

    // If connected, send session:delete to server (which will also delete from daemon)
    if (this.socket?.connected) {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          // Even if server doesn't respond, local deletion succeeded
          console.log('[SelfHostedAdapter] Delete server acknowledgment timeout - local deletion succeeded');
          resolve({ success: true });
        }, 5000);

        // Use session:delete instead of session:archive to trigger full deletion
        // from server DB, attachments, AND daemon's in-memory sessions
        this.socket!.emit('session:delete', {
          sessionId,
        }, (response: { success?: boolean; error?: string }) => {
          clearTimeout(timeout);
          if (response.success === false) {
            console.warn('[SelfHostedAdapter] Server delete failed:', response.error);
          } else {
            console.log('[SelfHostedAdapter] Session fully deleted from server and daemon');
          }
          // Local deletion already happened, so still return success
          resolve({ success: true });
        });
      });
    }

    return { success: true };
  }

  // ============================================
  // Messaging
  // ============================================

  sendMessage(sessionId: string, content: string, attachments?: MessageAttachment[]): boolean {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send message - not connected');
      return false;
    }

    if (!content.trim() && (!attachments || attachments.length === 0)) {
      console.warn('[SelfHostedAdapter] Cannot send empty message without attachments');
      return false;
    }

    console.log('[SelfHostedAdapter] Sending message to session:', sessionId, attachments ? `with ${attachments.length} attachments` : '');

    // BUG-002 FIX: Use message:send instead of session:input
    // session:input requires session ownership (createdBy === webDeviceId)
    // message:send allows messages to linked daemon sessions
    this.socket.emit('message:send', {
      sessionId,
      content,
      attachments: attachments || undefined,
    }, (response: { success: boolean; error?: string }) => {
      if (!response.success) {
        console.error('[SelfHostedAdapter] message:send failed:', response.error);
        this.emit('thinking:stop');
        this.emit('session:error', { error: response.error || 'Failed to send message' });
      } else {
        console.log('[SelfHostedAdapter] message:send acknowledged');
      }
    });

    // Start thinking indicator
    this.emit('thinking:start');

    return true;
  }

  /**
   * Send raw PTY input to a session.
   *
   * Unlike sendMessage (which uses message:send for SDK sessions),
   * this sends raw terminal input via session:input for PTY sessions.
   * Use this for PTY-only mode where you need character-by-character
   * terminal input (keyboard data, control sequences, etc).
   *
   * @param sessionId - The session to send input to
   * @param input - Raw terminal input (can include escape sequences)
   * @returns true if sent successfully
   */
  sendPtyInput(sessionId: string, input: string): boolean {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send PTY input - not connected');
      return false;
    }

    console.log('[SelfHostedAdapter] Sending PTY input to session:', sessionId, 'length:', input.length);

    // Use session:input for raw PTY terminal data
    // This goes directly to the PTY process stdin
    // Schema expects: sessionId, deviceId, content (not input)
    this.socket.emit('session:input', {
      sessionId,
      deviceId: this.linkedDaemonId || '',
      content: input,
    }, (response: { success: boolean; error?: string }) => {
      if (!response.success) {
        console.error('[SelfHostedAdapter] session:input failed:', response.error);
        this.emit('session:error', { error: response.error || 'Failed to send PTY input' });
      }
    });

    return true;
  }

  /**
   * Send terminal resize event to the daemon PTY
   * This triggers a SIGWINCH-like event to update terminal dimensions.
   *
   * @param sessionId - The session to resize
   * @param cols - New width in columns
   * @param rows - New height in rows
   * @returns true if sent successfully
   */
  sendResize(sessionId: string, cols: number, rows: number): boolean {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send resize - not connected');
      return false;
    }

    console.log('[SelfHostedAdapter] Sending resize to session:', sessionId, `${cols}x${rows}`);

    // Use session:resize to trigger PTY resize on daemon
    this.socket.emit('session:resize', {
      sessionId,
      cols,
      rows,
    }, (response: { success: boolean; error?: string }) => {
      if (!response.success) {
        console.error('[SelfHostedAdapter] session:resize failed:', response.error);
        this.emit('session:error', { error: response.error || 'Failed to resize session' });
      }
    });

    return true;
  }

  // ============================================
  // Self-Hosted Specific Methods
  // ============================================

  override async requestPairingCode(): Promise<PairingCode | null> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot request pairing code - not connected');
      return null;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Pairing code request timeout');
        resolve(null);
      }, 30000);

      this.socket!.emit('pairing:request', {}, (response: { code?: string; expiresIn?: number; error?: string }) => {
        clearTimeout(timeout);
        if (response.code) {
          resolve({
            code: response.code,
            expiresIn: response.expiresIn || 300,
            expiresAt: new Date(Date.now() + (response.expiresIn || 300) * 1000),
          });
        } else {
          console.error('[SelfHostedAdapter] Request pairing code failed:', response.error);
          resolve(null);
        }
      });
    });
  }

  override async confirmPairing(code: string): Promise<AuthTokens | null> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot confirm pairing - not connected');
      return null;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Confirm pairing timeout');
        resolve(null);
      }, 30000);

      this.socket!.emit('pairing:confirm', { code }, (response: { accessToken?: string; refreshToken?: string; expiresIn?: number; error?: string }) => {
        clearTimeout(timeout);
        if (response.accessToken) {
          this.authToken = response.accessToken;
          resolve({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken || '',
            expiresIn: response.expiresIn || 900,
          });
        } else {
          console.error('[SelfHostedAdapter] Confirm pairing failed:', response.error);
          resolve(null);
        }
      });
    });
  }

  override async stopDaemon(): Promise<boolean> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot stop daemon - not connected');
      return false;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Stop daemon timeout');
        resolve(false);
      }, 30000);

      this.socket!.emit('daemon:stop', {}, (response: { success?: boolean; error?: string }) => {
        clearTimeout(timeout);
        if (response.success) {
          this.daemonStatus = 'stopping';
        }
        resolve(response.success || false);
      });
    });
  }

  override async restartSession(sessionId: string): Promise<string | null> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot restart session - not connected');
      return null;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Restart session timeout');
        resolve(null);
      }, 30000);

      this.socket!.emit('session:restart', { session_id: sessionId, sessionId }, (response: { sessionId?: string; session_id?: string; error?: string }) => {
        clearTimeout(timeout);
        const newSessionId = response.sessionId || response.session_id;
        if (newSessionId) {
          resolve(newSessionId);
        } else {
          console.error('[SelfHostedAdapter] Restart session failed:', response.error);
          resolve(null);
        }
      });
    });
  }

  override getDaemonStatus(): DaemonStatus {
    return this.daemonStatus;
  }

  /**
   * Terminate all sessions (parent-only operation)
   * @param excludeParent If true, keeps the parent session running (default: true)
   */
  override async terminateAllSessions(
    excludeParent: boolean = true
  ): Promise<{
    success: boolean;
    terminatedCount: number;
    terminatedSessions: string[];
    failedSessions?: string[];
    error?: string;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot terminate all sessions - not connected');
      return { success: false, terminatedCount: 0, terminatedSessions: [], error: 'Not connected' };
    }

    const requestId = `terminate-all-${Date.now()}`;
    const requestingSessionId = this.getCurrentSessionId();

    console.log('[SelfHostedAdapter] Requesting terminate all sessions', {
      requestId,
      requestingSessionId,
      excludeParent,
    });

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Terminate all sessions timeout');
        resolve({ success: false, terminatedCount: 0, terminatedSessions: [], error: 'Request timeout' });
      }, 30000);

      // Listen for response
      const handleResponse = (response: {
        success: boolean;
        requestId: string;
        terminatedCount: number;
        terminatedSessions: string[];
        failedSessions?: string[];
        error?: string;
      }) => {
        if (response.requestId !== requestId) return;

        clearTimeout(timeout);
        this.socket?.off('sessions:all_terminated', handleResponse);

        console.log('[SelfHostedAdapter] Terminate all sessions response:', response);
        resolve(response);
      };

      this.socket!.on('sessions:all_terminated', handleResponse);

      this.socket!.emit('sessions:terminate_all', {
        requestingSessionId,
        excludeParent,
        requestId,
      });
    });
  }

  /**
   * Restart all sessions (parent-only operation)
   * @param excludeParent If true, keeps the parent session running (default: true)
   */
  override async restartAllSessions(
    excludeParent: boolean = true
  ): Promise<{
    success: boolean;
    restartedCount: number;
    sessionMapping: Record<string, string>;
    failedSessions?: string[];
    error?: string;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot restart all sessions - not connected');
      return { success: false, restartedCount: 0, sessionMapping: {}, error: 'Not connected' };
    }

    const requestId = `restart-all-${Date.now()}`;
    const requestingSessionId = this.getCurrentSessionId();

    console.log('[SelfHostedAdapter] Requesting restart all sessions', {
      requestId,
      requestingSessionId,
      excludeParent,
    });

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.error('[SelfHostedAdapter] Restart all sessions timeout');
        resolve({ success: false, restartedCount: 0, sessionMapping: {}, error: 'Request timeout' });
      }, 60000); // Longer timeout for restart (creates new sessions)

      // Listen for response
      const handleResponse = (response: {
        success: boolean;
        requestId: string;
        restartedCount: number;
        sessionMapping: Record<string, string>;
        failedSessions?: string[];
        error?: string;
      }) => {
        if (response.requestId !== requestId) return;

        clearTimeout(timeout);
        this.socket?.off('sessions:all_restarted', handleResponse);

        console.log('[SelfHostedAdapter] Restart all sessions response:', response);
        resolve(response);
      };

      this.socket!.on('sessions:all_restarted', handleResponse);

      this.socket!.emit('sessions:restart_all', {
        requestingSessionId,
        excludeParent,
        requestId,
      });
    });
  }

  /**
   * Get the current session ID (the one the user is viewing)
   * Used for authorization in parent-only operations
   */
  private getCurrentSessionId(): string | null {
    const store = useHappyStore.getState();
    return store.activeSessionId || null;
  }

  /**
   * Spawn a new session via daemon
   * This wraps createSession() to match the spawnSession interface used by HappyDashboard
   */
  override async spawnSession(
    directory: string,
    _machineId?: string
  ): Promise<{ ok: boolean; sessionId?: string; error?: string }> {
    console.log('[SelfHostedAdapter] spawnSession called with directory:', directory);

    const sessionId = await this.createSession({
      cwd: directory,
    });

    if (sessionId) {
      return { ok: true, sessionId };
    } else {
      return { ok: false, error: 'Failed to create session' };
    }
  }

  // ============================================
  // Daemon Control Methods
  // ============================================

  /**
   * Send a control command to the daemon via relay server
   * Supports: reconnect, restart, status, ping
   */
  async sendDaemonControl(
    command: 'reconnect' | 'restart' | 'status' | 'ping',
    options?: { timeoutMs?: number }
  ): Promise<{
    success: boolean;
    status?: string;
    error?: string;
    requestId?: string;
    data?: Record<string, unknown>;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send daemon control - not connected');
      return { success: false, error: 'Not connected to relay' };
    }

    if (!this.linkedDaemonDeviceId) {
      console.error('[SelfHostedAdapter] Cannot send daemon control - no daemon linked');
      return { success: false, error: 'No daemon linked' };
    }

    const requestId = `ctrl-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timeoutMs = options?.timeoutMs || 30000;

    console.log(`[SelfHostedAdapter] Sending daemon control: ${command} (requestId: ${requestId})`);

    return new Promise((resolve) => {
      let resolved = false;

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.error(`[SelfHostedAdapter] Daemon control ${command} timeout`);
          cleanup();
          resolve({ success: false, error: 'Command timeout', requestId });
        }
      }, timeoutMs);

      // Listen for daemon:control_response with matching requestId
      const onControlResponse = (data: {
        requestId?: string;
        status?: string;
        error?: string;
        [key: string]: unknown;
      }) => {
        if (data.requestId === requestId && !resolved) {
          resolved = true;
          cleanup();

          console.log(`[SelfHostedAdapter] Daemon control response for ${command}:`, data.status);

          if (data.status === 'error') {
            resolve({ success: false, error: data.error || 'Unknown error', requestId, data });
          } else {
            resolve({ success: true, status: data.status, requestId, data });
          }
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.socket?.off('daemon:control_response', onControlResponse);
      };

      // Register listener
      this.socket!.on('daemon:control_response', onControlResponse);

      // Send the control command
      this.socket!.emit(
        'daemon:control',
        {
          command,
          deviceId: this.linkedDaemonDeviceId,
          requestId,
        },
        (ackResponse: { success?: boolean; error?: string; requestId?: string }) => {
          if (!ackResponse.success && !resolved) {
            resolved = true;
            cleanup();
            console.error(`[SelfHostedAdapter] Daemon control ${command} failed to forward:`, ackResponse.error);
            resolve({ success: false, error: ackResponse.error || 'Failed to forward command', requestId });
          }
          // If success, wait for daemon:control_response event
        }
      );
    });
  }

  /**
   * Request daemon to force reconnection to relay server
   */
  async requestDaemonReconnect(): Promise<boolean> {
    const result = await this.sendDaemonControl('reconnect');
    if (result.success) {
      // After reconnect command, daemon will disconnect and reconnect
      // We should expect temporary disconnection
      console.log('[SelfHostedAdapter] Daemon reconnect requested - expect temporary disconnection');
      this.daemonStatus = 'stopping';
      this.emit('daemon:status', {
        deviceId: this.linkedDaemonDeviceId || '',
        status: 'stopping',
        sessionCount: 0,
      });
    }
    return result.success;
  }

  /**
   * Request full daemon restart (terminate sessions, reconnect)
   */
  async requestDaemonRestart(): Promise<boolean> {
    const result = await this.sendDaemonControl('restart');
    if (result.success) {
      console.log('[SelfHostedAdapter] Daemon restart requested - sessions will be terminated');
      this.daemonStatus = 'stopping';
      this.emit('daemon:status', {
        deviceId: this.linkedDaemonDeviceId || '',
        status: 'stopping',
        sessionCount: 0,
      });
      // Clear local sessions as they'll be terminated
      this.activeSessions.clear();
      this.emit('session:list', []);
    }
    return result.success;
  }

  /**
   * Request detailed daemon status
   */
  async requestDaemonStatus(): Promise<{
    success: boolean;
    daemonStatus?: Record<string, unknown>;
    sessionStats?: Record<string, unknown>;
    activeSessions?: Array<Record<string, unknown>>;
    error?: string;
  }> {
    const result = await this.sendDaemonControl('status');
    if (result.success && result.data) {
      return {
        success: true,
        daemonStatus: result.data.daemonStatus as Record<string, unknown>,
        sessionStats: result.data.sessionStats as Record<string, unknown>,
        activeSessions: result.data.activeSessions as Array<Record<string, unknown>>,
      };
    }
    return { success: false, error: result.error };
  }

  /**
   * Ping daemon to check connectivity
   */
  async pingDaemon(): Promise<{ success: boolean; latencyMs?: number; error?: string }> {
    const startTime = Date.now();
    const result = await this.sendDaemonControl('ping', { timeoutMs: 10000 });
    const latencyMs = Date.now() - startTime;

    if (result.success) {
      return { success: true, latencyMs };
    }
    return { success: false, error: result.error };
  }

  /**
   * BUG-001 FIX Part 2: Link to daemon before requesting sessions
   * The relay server only returns sessions for the daemon that web client is linked to.
   * Flow: daemon:list -> daemon:link -> session:list
   */
  private linkToDaemonAndRequestSessions(): void {
    if (!this.socket?.connected) return;

    console.log('[SelfHostedAdapter] Requesting daemon list...');

    // Step 1: Get list of available daemons
    this.socket.emit('daemon:list', {}, (response: {
      success: boolean;
      daemons?: Array<{
        deviceId: string;
        connectedAt: string;
        authenticated: boolean;
        sessionCount: number;
      }>;
      error?: string;
    }) => {
      if (!response.success) {
        console.warn('[SelfHostedAdapter] daemon:list failed:', response.error);
        return;
      }

      const daemons = response.daemons || [];
      console.log('[SelfHostedAdapter] Found', daemons.length, 'daemon(s):', daemons);

      // Emit daemon:list event to populate store's daemon registry
      this.emit('daemon:list', daemons);

      if (daemons.length === 0) {
        console.log('[SelfHostedAdapter] No daemons available to link to');
        // Update daemon status
        this.daemonStatus = 'stopped';
        this.emit('daemon:status', { deviceId: '', status: 'stopped', sessionCount: 0 });
        return;
      }

      // Step 2: Link to daemon - prefer previously selected daemon, fall back to first
      // FIX: On reconnect, use the store's selectedDaemonId to maintain user's selection
      const storeSelectedDaemonId = useHappyStore.getState().selectedDaemonId;
      let targetDaemon = daemons[0]; // default to first

      if (storeSelectedDaemonId) {
        const previouslySelected = daemons.find(d => d.deviceId === storeSelectedDaemonId);
        if (previouslySelected) {
          targetDaemon = previouslySelected;
          console.log('[SelfHostedAdapter] Reconnecting to previously selected daemon:', targetDaemon.deviceId);
        } else {
          console.log('[SelfHostedAdapter] Previously selected daemon not in list, falling back to first');
        }
      }
      console.log('[SelfHostedAdapter] Linking to daemon:', targetDaemon.deviceId);

      this.socket!.emit('daemon:link', { deviceId: targetDaemon.deviceId }, (linkResponse: {
        success: boolean;
        error?: string;
      }) => {
        if (!linkResponse.success) {
          console.warn('[SelfHostedAdapter] daemon:link failed:', linkResponse.error);
          return;
        }

        console.log('[SelfHostedAdapter] Successfully linked to daemon:', targetDaemon.deviceId);
        this.linkedDaemonDeviceId = targetDaemon.deviceId;
        this.daemonStatus = 'running';
        this.emit('daemon:status', {
          deviceId: targetDaemon.deviceId,
          status: 'running',
          sessionCount: targetDaemon.sessionCount,
          connectedAt: targetDaemon.connectedAt,
        });

        // Step 3: Now request session list (will have linked daemon's sessions)
        setTimeout(() => {
          console.log('[SelfHostedAdapter] Requesting session list after daemon link...');
          this.requestSessionList();
        }, 100);
      });
    });
  }

  /**
   * Public method to link to a specific daemon (called when user selects different daemon)
   * FIX: Daemon selection was not updating linkedDaemonDeviceId
   */
  public linkToDaemon(deviceId: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.socket?.connected) {
        console.error('[SelfHostedAdapter] Cannot link to daemon - not connected');
        resolve(false);
        return;
      }

      if (this.linkedDaemonDeviceId === deviceId) {
        console.log('[SelfHostedAdapter] Already linked to daemon:', deviceId);
        resolve(true);
        return;
      }

      console.log('[SelfHostedAdapter] Switching daemon link to:', deviceId);

      this.socket.emit('daemon:link', { deviceId }, (linkResponse: {
        success: boolean;
        error?: string;
      }) => {
        if (!linkResponse.success) {
          console.warn('[SelfHostedAdapter] daemon:link failed:', linkResponse.error);
          resolve(false);
          return;
        }

        console.log('[SelfHostedAdapter] Successfully linked to daemon:', deviceId);
        this.linkedDaemonDeviceId = deviceId;
        this.daemonStatus = 'running';
        this.emit('daemon:status', {
          deviceId,
          status: 'running',
          sessionCount: 0, // Will be updated by session list
        });
        this.emit('daemon:linked', { deviceId });

        // Refresh session list for new daemon
        setTimeout(() => {
          console.log('[SelfHostedAdapter] Requesting session list after daemon switch...');
          this.requestSessionList();
        }, 100);

        resolve(true);
      });
    });
  }

  /**
   * Get the currently linked daemon device ID
   */
  public getLinkedDaemonId(): string | null {
    return this.linkedDaemonDeviceId;
  }

  /**
   * Request session list from server
   * FIX for BUG-001: Use ack callback to receive session list response from relay
   */
  requestSessionList(): void {
    if (!this.socket?.connected) return;

    console.log('[SelfHostedAdapter] Requesting session list with callback...');

    // Use callback to receive response from relay server
    // The relay's session:list handler uses ack() to respond
    this.socket.emit('session:list', {}, (response: {
      success: boolean;
      sessions?: Array<{
        sessionId: string;
        state?: string;
        createdBy?: string;
        createdAt?: string;
        isOwner?: boolean;
      }>;
      error?: string;
    }) => {
      if (!response.success) {
        console.warn('[SelfHostedAdapter] Session list request failed:', response.error);
        return;
      }

      const sessions = response.sessions || [];
      console.log('[SelfHostedAdapter] Session list response received:', sessions.length, 'sessions');

      // Clear existing activeSessions before repopulating
      // This ensures stale sessions from previous daemon runs are removed
      this.activeSessions.clear();

      // Normalize and store sessions
      const normalized: NormalizedSession[] = [];
      for (const raw of sessions) {
        const session = this.normalizeRawSession(raw);
        if (session) {
          this.activeSessions.set(session.sessionId, session);
          normalized.push(session);
        }
      }

      // Emit session list to store
      this.emit('session:list', normalized);

      // FIX: Fetch message history for all sessions after reconnection
      // This ensures messages persist across browser refresh
      console.log('[SelfHostedAdapter] Fetching message history for', normalized.length, 'session(s)');
      for (const session of normalized) {
        // Stagger requests slightly to avoid overwhelming the server
        setTimeout(() => {
          this.requestMessageHistory(session.sessionId);
        }, 50 * normalized.indexOf(session));
      }

      // SRP: Check for persisted session and request rehydration
      this.attemptSessionRehydration(normalized);
    });
  }

  /**
   * SRP: Attempt to rehydrate a previously selected session after reconnect
   * This preserves session selection and tool state across browser refresh
   */
  private attemptSessionRehydration(sessions: NormalizedSession[]): void {
    const persistedSessionId = this.getPersistedSession();
    if (!persistedSessionId) {
      console.log('[SelfHostedAdapter] No persisted session to rehydrate');
      return;
    }

    // Check if persisted session exists in current session list
    const sessionExists = sessions.some(s => s.sessionId === persistedSessionId);
    if (!sessionExists) {
      console.log('[SelfHostedAdapter] Persisted session not found in list, clearing:', persistedSessionId);
      this.clearPersistedSession();
      return;
    }

    console.log('[SelfHostedAdapter] Attempting SRP rehydration for session:', persistedSessionId);

    // Request rehydration with lastEventId=0 to get full state
    this.requestRehydration(persistedSessionId, 0)
      .then((result) => {
        if (result.success && result.data) {
          console.log('[SelfHostedAdapter] SRP rehydration successful:', {
            sessionId: persistedSessionId,
            state: result.data.state,
            activeRuns: result.data.activeRuns?.length || 0,
            runningTools: result.data.runningTools?.length || 0,
            lastEventId: result.data.lastEventId,
          });

          // Emit rehydration event for store to process
          this.emit('session:rehydrated', {
            sessionId: persistedSessionId,
            ...result.data,
          });

          // Auto-join the rehydrated session
          this.joinSession(persistedSessionId);

          // Request event replay for any events we might have missed
          if (result.data.lastEventId && result.data.lastEventId > 0) {
            this.requestEventReplay(persistedSessionId, 0, 500);
          }
        } else {
          console.warn('[SelfHostedAdapter] SRP rehydration failed:', result.error);
          // Don't clear persisted session on transient failure - may succeed on retry
        }
      })
      .catch((error) => {
        console.error('[SelfHostedAdapter] SRP rehydration error:', error);
      });
  }

  /**
   * Request message history for a session from SQLite persistence
   *
   * FIX: Changed from emitting 'messages:history' to 'message:fetch'
   * The server's messageSync.ts handler expects 'message:fetch' and uses callback pattern
   */
  requestMessageHistory(sessionId: string, limit: number = 100): void {
    if (!this.socket?.connected) return;

    console.log('[SelfHostedAdapter] Fetching message history for session:', sessionId);

    this.socket.emit(
      'message:fetch',
      { sessionId, limit },
      (response: { success: boolean; messages?: unknown[]; total?: number; error?: string }) => {
        if (!response.success) {
          console.warn('[SelfHostedAdapter] Failed to fetch message history:', response.error);
          return;
        }

        console.log('[SelfHostedAdapter] Received message history:', {
          sessionId,
          count: response.messages?.length || 0,
          total: response.total,
        });

        // Convert server response to the expected format and process through handleRawHistory
        if (response.messages && response.messages.length > 0) {
          this.handleRawHistory({
            sessionId,
            session_id: sessionId,
            messages: response.messages,
          });
        }
      }
    );
  }

  /**
   * Request fresh session sync from server
   */
  requestSync(): void {
    if (!this.socket?.connected) {
      console.warn('[SelfHostedAdapter] Cannot request sync - not connected');
      return;
    }

    console.log('[SelfHostedAdapter] Requesting session sync from server');
    this.socket.emit('sync:request');
  }

  // ============================================
  // Heartbeat Methods
  // ============================================

  /**
   * Initialize and start the heartbeat monitor
   * Called after successful connection/reconnection
   */
  private initializeHeartbeatMonitor(): void {
    // Stop existing monitor if any
    if (this.heartbeatMonitor) {
      this.heartbeatMonitor.stop();
    }

    // Create new monitor with callbacks
    this.heartbeatMonitor = new HeartbeatMonitor(
      this.heartbeatConfig,
      // onPing - send ping to server
      () => {
        if (this.socket?.connected) {
          this.socket.emit('ping');
        }
      },
      // onHealthy - connection recovered
      () => {
        console.log('[SelfHostedAdapter] Heartbeat healthy');
        this.emit('heartbeat:healthy');
      },
      // onUnhealthy - missed pong(s) but not yet reconnecting
      (missedPongs: number) => {
        console.warn(`[SelfHostedAdapter] Heartbeat unhealthy, missed pongs: ${missedPongs}`);
        this.emit('heartbeat:unhealthy', missedPongs);
      },
      // onReconnecting - max pongs missed, trigger reconnect
      () => {
        console.warn('[SelfHostedAdapter] Heartbeat failed, triggering reconnect');
        this.emit('heartbeat:reconnecting');
        this.setConnectionState('reconnecting');

        // Disconnect and let socket.io auto-reconnect
        if (this.socket?.connected) {
          this.socket.disconnect();
        }
      }
    );

    // Start monitoring
    this.heartbeatMonitor.start();
  }

  /**
   * Get current heartbeat state
   * Used by UI components to show connection health
   */
  getHeartbeatState(): HeartbeatState | null {
    return this.heartbeatMonitor?.getState() || null;
  }

  /**
   * Configure heartbeat settings
   * @param config - Partial heartbeat configuration to merge
   */
  configureHeartbeat(config: Partial<HeartbeatConfig>): void {
    this.heartbeatConfig = { ...this.heartbeatConfig, ...config };
    console.log('[SelfHostedAdapter] Heartbeat config updated:', this.heartbeatConfig);

    // If monitor is running, restart with new config
    if (this.heartbeatMonitor?.getState().isActive) {
      this.initializeHeartbeatMonitor();
    }
  }

  // ============================================
  // SelfHostedService Sync (Session Title Fix)
  // ============================================

  /**
   * Synchronize selfHostedService singleton with adapter connection
   * This allows the store to use selfHostedService for session title updates
   *
   * ROOT CAUSE: The store imports selfHostedService singleton for updateSessionTitle()
   * but the singleton's socket is never connected (adapter creates separate socket).
   * This method ensures the singleton gets connected when adapter connects.
   */
  private syncSelfHostedService(): void {
    if (!this.serverUrl) {
      console.warn('[SelfHostedAdapter] Cannot sync selfHostedService: no serverUrl');
      return;
    }

    // Connect the singleton with same URL and token
    console.log('[SelfHostedAdapter] Syncing selfHostedService with relay connection...');

    selfHostedService.connect(this.serverUrl)
      .then(() => {
        console.log('[SelfHostedAdapter] selfHostedService synced successfully');

        // If we have an auth token, set it on the service too
        if (this.authToken) {
          selfHostedService.setAccessToken(this.authToken);
        }
      })
      .catch((error) => {
        console.warn('[SelfHostedAdapter] selfHostedService sync failed (non-fatal):', error);
        // Non-fatal - adapter connection still works, just title updates won't work
      });
  }

  // ============================================
  // Settings Methods (BUG-003 FIX)
  // ============================================

  /**
   * Send settings to daemon via relay server
   * FIX for BUG-003: Integrated settings relay into SelfHostedAdapter
   *
   * @param settings - Settings to apply (permissionMode, modelOption, sessionId)
   * @returns Promise resolving to success/error result
   */
  async sendSettings(settings: {
    permissionMode: 'default' | 'accept-edits' | 'plan-mode' | 'yolo-mode';
    modelOption: 'cli-settings' | 'opus-4-5' | 'sonnet-4-5' | 'haiku-4-5' | 'opus-then-sonnet';
    sessionId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send settings - not connected');
      return { success: false, error: 'Not connected to relay server' };
    }

    console.log('[SelfHostedAdapter] Sending settings to daemon:', settings);

    return new Promise((resolve) => {
      const requestId = `settings-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      let resolved = false;

      // Timeout after 5 seconds
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn('[SelfHostedAdapter] Settings request timed out');
          cleanup();
          resolve({ success: false, error: 'Settings update timeout' });
        }
      }, 5000);

      // Listen for success response
      const handleSettingsApplied = (data: {
        requestId?: string;
        success?: boolean;
        permissionMode?: string;
        modelOption?: string;
        deviceId?: string;
        timestamp?: string;
      }) => {
        if (data.requestId === requestId && !resolved) {
          resolved = true;
          console.log('[SelfHostedAdapter] Settings applied:', data);
          cleanup();
          resolve({ success: true });
        }
      };

      // Listen for error response
      const handleSettingsError = (data: {
        requestId?: string;
        error: string;
        code?: string;
        deviceId?: string;
        timestamp?: string;
      }) => {
        if (data.requestId === requestId && !resolved) {
          resolved = true;
          console.error('[SelfHostedAdapter] Settings error:', data.error);
          cleanup();
          resolve({ success: false, error: data.error });
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.socket?.off('daemon:settings_applied', handleSettingsApplied);
        this.socket?.off('daemon:settings_error', handleSettingsError);
      };

      // Register event listeners
      this.socket!.on('daemon:settings_applied', handleSettingsApplied);
      this.socket!.on('daemon:settings_error', handleSettingsError);

      // Emit settings to daemon via relay
      this.socket!.emit('daemon:settings', {
        permissionMode: settings.permissionMode,
        modelOption: settings.modelOption,
        sessionId: settings.sessionId,
        requestedBy: this.webDeviceId || 'web-ui',
        requestId,
        timestamp: Date.now(),
      });
    });
  }

  // ============================================
  // AI Title Generation Methods
  // ============================================

  /**
   * Request AI-powered title generation from daemon
   * Uses the daemon's Anthropic SDK with user's Claude Code CLI Max subscription
   *
   * @param message - The user message to generate a title from
   * @param sessionId - Session ID to associate the title with
   * @returns Promise resolving to generated title or null on failure
   */
  async requestTitleGeneration(
    message: string,
    sessionId: string
  ): Promise<{ title: string; aiGenerated: boolean } | null> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot request title - not connected');
      return null;
    }

    if (!this.linkedDaemonDeviceId) {
      console.warn('[SelfHostedAdapter] No daemon linked - cannot generate AI title');
      return null;
    }

    const requestId = `title-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    console.log('[SelfHostedAdapter] Requesting AI title generation:', {
      sessionId,
      messagePreview: message.slice(0, 50),
      requestId,
    });

    return new Promise((resolve) => {
      let resolved = false;

      // Timeout after 10 seconds (AI generation may take a moment)
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn('[SelfHostedAdapter] Title generation timeout');
          cleanup();
          resolve(null);
        }
      }, 10000);

      // Listen for title:generated response with matching requestId
      const handleTitleGenerated = (data: {
        title?: string;
        aiGenerated?: boolean;
        sessionId?: string;
        requestId?: string;
        error?: string;
      }) => {
        if (data.requestId === requestId && !resolved) {
          resolved = true;
          cleanup();

          if (data.error) {
            console.warn('[SelfHostedAdapter] Title generation error:', data.error);
            resolve(null);
          } else {
            console.log('[SelfHostedAdapter] Title generated:', data.title);
            resolve({
              title: data.title || 'New Session',
              aiGenerated: data.aiGenerated || false,
            });
          }
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.socket?.off('title:generated', handleTitleGenerated);
      };

      // Register listener
      this.socket!.on('title:generated', handleTitleGenerated);

      // Send title generation request
      this.socket!.emit('title:generate', {
        message,
        sessionId,
        requestId,
      }, (ackResponse: { success?: boolean; error?: string; requestId?: string }) => {
        if (!ackResponse.success && !resolved) {
          resolved = true;
          cleanup();
          console.error('[SelfHostedAdapter] Title request failed to forward:', ackResponse.error);
          resolve(null);
        }
        // If success, wait for title:generated event
      });
    });
  }

  // ============================================
  // Hybrid Control Event Methods
  // Phase: Hybrid Orchestrator Migration
  // ============================================

  /**
   * Sequence counter for control events
   */
  private controlEventSequence = 0;

  /**
   * Get next sequence number for control events
   */
  private getNextControlSequence(): number {
    return ++this.controlEventSequence;
  }

  /**
   * Generate a unique request ID for control events
   */
  private generateControlRequestId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Send a control:message event to the daemon
   *
   * @param sessionId - Target session ID
   * @param content - Message content
   * @param options - Optional attachments and requestId
   * @returns Promise with acknowledgment response
   */
  async sendControlMessage(
    sessionId: string,
    content: string,
    options?: {
      attachments?: Array<{
        id: string;
        type: 'file' | 'image' | 'screenshot';
        name: string;
        size: number;
        mimeType?: string;
        url?: string;
        base64?: string;
      }>;
      requestId?: string;
    }
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send control:message - not connected');
      return { success: false, error: 'Not connected' };
    }

    const requestId = options?.requestId || this.generateControlRequestId('msg');
    const seq = this.getNextControlSequence();

    console.log('[SelfHostedAdapter] Sending control:message:', {
      sessionId,
      contentPreview: content.slice(0, 50),
      attachmentCount: options?.attachments?.length || 0,
      requestId,
      seq,
    });

    return new Promise((resolve) => {
      this.socket!.emit('control:message', {
        eventType: 'control:message',
        sessionId,
        timestamp: Date.now() / 1000,
        seq,
        content,
        attachments: options?.attachments,
        requestId,
      }, (response: { success: boolean; messageId?: string; error?: string }) => {
        if (response.success) {
          console.log('[SelfHostedAdapter] control:message acknowledged:', response.messageId);
        } else {
          console.error('[SelfHostedAdapter] control:message failed:', response.error);
        }
        resolve(response);
      });
    });
  }

  /**
   * Send a control:permission event to respond to a permission request
   *
   * @param sessionId - Target session ID
   * @param permissionId - Permission request ID being responded to
   * @param action - The action to take (approve, deny, approve_all, deny_all)
   * @returns Promise with acknowledgment response
   */
  async sendControlPermission(
    sessionId: string,
    permissionId: string,
    action: 'approve' | 'deny' | 'approve_all' | 'deny_all'
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send control:permission - not connected');
      return { success: false, error: 'Not connected' };
    }

    const requestId = this.generateControlRequestId('perm');
    const seq = this.getNextControlSequence();

    console.log('[SelfHostedAdapter] Sending control:permission:', {
      sessionId,
      permissionId,
      action,
      requestId,
      seq,
    });

    return new Promise((resolve) => {
      this.socket!.emit('control:permission', {
        eventType: 'control:permission',
        sessionId,
        timestamp: Date.now() / 1000,
        seq,
        permissionId,
        action,
        requestId,
      }, (response: { success: boolean; error?: string }) => {
        if (response.success) {
          console.log('[SelfHostedAdapter] control:permission acknowledged');
        } else {
          console.error('[SelfHostedAdapter] control:permission failed:', response.error);
        }
        resolve(response);
      });
    });
  }

  /**
   * Send a control:interrupt event to interrupt the current operation
   *
   * @param sessionId - Target session ID
   * @param interruptType - Type of interrupt (soft or hard)
   * @param reason - Optional reason for the interrupt
   * @returns Promise with acknowledgment response
   */
  async sendControlInterrupt(
    sessionId: string,
    interruptType: 'soft' | 'hard' = 'soft',
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send control:interrupt - not connected');
      return { success: false, error: 'Not connected' };
    }

    const requestId = this.generateControlRequestId('int');
    const seq = this.getNextControlSequence();

    console.log('[SelfHostedAdapter] Sending control:interrupt:', {
      sessionId,
      interruptType,
      reason,
      requestId,
      seq,
    });

    return new Promise((resolve) => {
      this.socket!.emit('control:interrupt', {
        eventType: 'control:interrupt',
        sessionId,
        timestamp: Date.now() / 1000,
        seq,
        interruptType,
        reason,
        requestId,
      }, (response: { success: boolean; error?: string }) => {
        if (response.success) {
          console.log('[SelfHostedAdapter] control:interrupt acknowledged');
        } else {
          console.error('[SelfHostedAdapter] control:interrupt failed:', response.error);
        }
        resolve(response);
      });
    });
  }

  /**
   * Send a control:settings event to update session settings
   *
   * @param sessionId - Target session ID
   * @param settings - Settings to update
   * @returns Promise with acknowledgment response
   */
  async sendControlSettings(
    sessionId: string,
    settings: {
      permissionMode?: 'default' | 'accept-edits' | 'plan-mode' | 'yolo-mode';
      modelOption?: 'cli-settings' | 'opus-4-5' | 'sonnet-4-5' | 'haiku-4-5' | 'opus-then-sonnet';
    }
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send control:settings - not connected');
      return { success: false, error: 'Not connected' };
    }

    const requestId = this.generateControlRequestId('set');
    const seq = this.getNextControlSequence();

    console.log('[SelfHostedAdapter] Sending control:settings:', {
      sessionId,
      settings,
      requestId,
      seq,
    });

    return new Promise((resolve) => {
      this.socket!.emit('control:settings', {
        eventType: 'control:settings',
        sessionId,
        timestamp: Date.now() / 1000,
        seq,
        permissionMode: settings.permissionMode,
        modelOption: settings.modelOption,
        requestId,
      }, (response: { success: boolean; error?: string }) => {
        if (response.success) {
          console.log('[SelfHostedAdapter] control:settings acknowledged');
        } else {
          console.error('[SelfHostedAdapter] control:settings failed:', response.error);
        }
        resolve(response);
      });
    });
  }

  /**
   * Send a control:slash_command event to execute a slash command
   *
   * @param sessionId - Target session ID
   * @param command - Slash command to execute (e.g., "/commit", "/help")
   * @param args - Optional arguments for the command
   * @returns Promise with acknowledgment response
   */
  async sendControlSlashCommand(
    sessionId: string,
    command: string,
    args?: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot send control:slash_command - not connected');
      return { success: false, error: 'Not connected' };
    }

    const requestId = this.generateControlRequestId('cmd');
    const seq = this.getNextControlSequence();

    console.log('[SelfHostedAdapter] Sending control:slash_command:', {
      sessionId,
      command,
      args,
      requestId,
      seq,
    });

    return new Promise((resolve) => {
      this.socket!.emit('control:slash_command', {
        eventType: 'control:slash_command',
        sessionId,
        timestamp: Date.now() / 1000,
        seq,
        command,
        args,
        requestId,
      }, (response: { success: boolean; error?: string }) => {
        if (response.success) {
          console.log('[SelfHostedAdapter] control:slash_command acknowledged');
        } else {
          console.error('[SelfHostedAdapter] control:slash_command failed:', response.error);
        }
        resolve(response);
      });
    });
  }

  // ============================================
  // TSRP Methods (Tool Stream Resolution Protocol)
  // ============================================

  private static readonly SESSION_STORAGE_KEY = 'vibe-remote-current-session';

  /**
   * Persist current session ID to localStorage for refresh recovery
   */
  persistCurrentSession(sessionId: string): void {
    try {
      localStorage.setItem(SelfHostedSocketAdapter.SESSION_STORAGE_KEY, sessionId);
      console.log('[SelfHostedAdapter] Persisted current session:', sessionId);
    } catch (e) {
      console.warn('[SelfHostedAdapter] Failed to persist session to localStorage:', e);
    }
  }

  /**
   * Get persisted session ID from localStorage
   */
  getPersistedSession(): string | null {
    try {
      return localStorage.getItem(SelfHostedSocketAdapter.SESSION_STORAGE_KEY);
    } catch (e) {
      console.warn('[SelfHostedAdapter] Failed to read session from localStorage:', e);
      return null;
    }
  }

  /**
   * Clear persisted session ID from localStorage
   */
  clearPersistedSession(): void {
    try {
      localStorage.removeItem(SelfHostedSocketAdapter.SESSION_STORAGE_KEY);
      console.log('[SelfHostedAdapter] Cleared persisted session');
    } catch (e) {
      console.warn('[SelfHostedAdapter] Failed to clear session from localStorage:', e);
    }
  }

  /**
   * Request session rehydration from server (SRP)
   *
   * Used on page refresh to restore session state.
   *
   * @param sessionId - Session to rehydrate
   * @param lastEventId - Last known event ID (for incremental replay)
   * @returns Promise with rehydration response
   */
  async requestRehydration(
    sessionId: string,
    lastEventId: number = 0
  ): Promise<{
    success: boolean;
    lastEventId?: number;
    messages?: unknown[];
    toolCalls?: unknown[];
    activeRun?: unknown;
    error?: string;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot request rehydration - not connected');
      return { success: false, error: 'Not connected' };
    }

    console.log('[SelfHostedAdapter] Requesting session rehydration:', {
      sessionId,
      lastEventId,
    });

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[SelfHostedAdapter] Rehydration request timed out');
        resolve({ success: false, error: 'Rehydration timeout' });
      }, 10000);

      this.socket!.emit('session:rehydrate:request', {
        sessionId,
        lastEventId,
      }, (response: {
        success: boolean;
        sessionId: string;
        lastEventId: number;
        session?: unknown;
        messages?: unknown[];
        toolCalls?: unknown[];
        activeRun?: unknown;
        error?: string;
      }) => {
        clearTimeout(timeout);

        if (response.success) {
          console.log('[SelfHostedAdapter] Rehydration successful:', {
            sessionId: response.sessionId,
            lastEventId: response.lastEventId,
            messagesCount: response.messages?.length,
            toolCallsCount: response.toolCalls?.length,
          });
        } else {
          console.warn('[SelfHostedAdapter] Rehydration failed:', response.error);
        }

        resolve(response);
      });
    });
  }

  /**
   * Request session stop (cancel all running tools and runs)
   *
   * @param sessionId - Session to stop
   * @param reason - Reason for stopping (e.g., 'user_cancelled')
   * @returns Promise with stop response
   */
  async requestSessionStop(
    sessionId: string,
    reason: string = 'user_cancelled'
  ): Promise<{
    success: boolean;
    runsCanceled?: number;
    toolsCanceled?: number;
    error?: string;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot stop session - not connected');
      return { success: false, error: 'Not connected' };
    }

    console.log('[SelfHostedAdapter] Requesting session stop:', { sessionId, reason });

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[SelfHostedAdapter] Session stop request timed out');
        resolve({ success: false, error: 'Stop request timeout' });
      }, 5000);

      this.socket!.emit('session:stop', {
        sessionId,
        reason,
      }, (response: {
        sessionId: string;
        runsCanceled: number;
        toolsCanceled: number;
        stoppedAt: string;
        reason?: string;
      }) => {
        clearTimeout(timeout);

        console.log('[SelfHostedAdapter] Session stop response:', {
          runsCanceled: response.runsCanceled,
          toolsCanceled: response.toolsCanceled,
        });

        resolve({
          success: true,
          runsCanceled: response.runsCanceled,
          toolsCanceled: response.toolsCanceled,
        });
      });
    });
  }

  /**
   * Request incremental event replay (SRP)
   *
   * @param sessionId - Session ID
   * @param afterEventId - Return events after this ID
   * @param limit - Maximum events to return
   * @returns Promise with event replay response
   */
  async requestEventReplay(
    sessionId: string,
    afterEventId: number = 0,
    limit: number = 100
  ): Promise<{
    success: boolean;
    events?: Array<{
      id: number;
      eventType: string;
      payload: Record<string, unknown>;
      createdAt: string;
    }>;
    lastEventId?: number;
    hasMore?: boolean;
    error?: string;
  }> {
    if (!this.socket?.connected) {
      console.error('[SelfHostedAdapter] Cannot request event replay - not connected');
      return { success: false, error: 'Not connected' };
    }

    console.log('[SelfHostedAdapter] Requesting event replay:', {
      sessionId,
      afterEventId,
      limit,
    });

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[SelfHostedAdapter] Event replay request timed out');
        resolve({ success: false, error: 'Event replay timeout' });
      }, 10000);

      this.socket!.emit('session:events:replay', {
        sessionId,
        after: afterEventId,
        limit,
      }, (response: {
        success: boolean;
        sessionId: string;
        events: Array<{
          id: number;
          eventType: string;
          payload: Record<string, unknown>;
          createdAt: string;
        }>;
        lastEventId: number;
        hasMore: boolean;
        error?: string;
      }) => {
        clearTimeout(timeout);

        if (response.success) {
          console.log('[SelfHostedAdapter] Event replay successful:', {
            eventsCount: response.events?.length,
            lastEventId: response.lastEventId,
            hasMore: response.hasMore,
          });
        } else {
          console.warn('[SelfHostedAdapter] Event replay failed:', response.error);
        }

        resolve(response);
      });
    });
  }
}
